import type { AuditLog } from '@byte-store-control/shared';
import { getActionDisplayText } from '../components/AuditHistory/ActionBadge';

/**
 * Escape a value for CSV format
 */
function escapeCSVValue(value: string | undefined | null): string {
  if (value === undefined || value === null) {
    return '';
  }
  // If the value contains a comma, newline, or double quote, wrap it in quotes
  if (value.includes(',') || value.includes('\n') || value.includes('"')) {
    // Escape double quotes by doubling them
    return `"${value.replace(/"/g, '""')}"`;
  }
  return value;
}

/**
 * Convert audit logs to CSV format
 */
export function auditLogsToCSV(logs: AuditLog[]): string {
  const headers = [
    'Timestamp',
    'User',
    'User Email',
    'Action',
    'Action Type',
    'Store Number',
    'Entity ID',
    'IP Address',
    'Environment',
    'Request ID',
    'Changes',
  ];

  const rows = logs.map((log) => [
    escapeCSVValue(new Date(log.timestamp).toISOString()),
    escapeCSVValue(log.userName),
    escapeCSVValue(log.userEmail),
    escapeCSVValue(getActionDisplayText(log.action)),
    escapeCSVValue(log.entityType),
    escapeCSVValue(log.storeNumber),
    escapeCSVValue(log.entityId),
    escapeCSVValue(log.ipAddress),
    escapeCSVValue(log.environment),
    escapeCSVValue(log.requestId),
    escapeCSVValue(log.changes ? JSON.stringify(log.changes) : ''),
  ]);

  const csvContent = [headers.join(','), ...rows.map((row) => row.join(','))].join('\n');

  return csvContent;
}

/**
 * Download a string as a file
 */
export function downloadFile(content: string, filename: string, mimeType: string): void {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

/**
 * Export audit logs to CSV and trigger download
 */
export function exportAuditLogsToCSV(logs: AuditLog[], filename?: string): void {
  const csvContent = auditLogsToCSV(logs);
  const date = new Date().toISOString().split('T')[0];
  const finalFilename = filename || `audit-logs-${date}.csv`;
  downloadFile(csvContent, finalFilename, 'text/csv;charset=utf-8;');
}
